import type { ConversationSummary } from "@teamna/shared";
import { useNavigate } from "@tanstack/react-router";
import {
  Button,
  Composer,
  ConversationThread,
  ErrorState,
  MegaphoneIcon,
  SkeletonList,
  SuccessNotice,
} from "../components/index.ts";
import { useLabels } from "@teamna/web-client/hooks/use-labels.ts";
import { useConversations } from "@teamna/web-client/hooks/use-messaging.ts";
import { useActiveBusiness } from "@teamna/web-client/hooks/use-owner.ts";
import { OwnerGate, useOwnerNotice } from "./shell.tsx";

/**
 * One conversation, read from the business's chair.
 *
 * The bubbles and the read mark are `ConversationThread`, shared with the member side; the roles
 * are flipped by the API's `mine`. What this file decides is the header and the footer:
 *
 * - A direct thread carries the member's phone as a `tel:` link, because the reason a staff member
 *   opens a thread on the pool deck is often to chase somebody, and a number that has to be retyped
 *   into the dialler is a number that does not get called. The footer is the composer.
 * - The announcement channel has no composer. A post goes through the composer Panel with a
 *   recipient count and a confirm step, so the footer is the button that opens it.
 *
 * `ThreadBody` is the thread itself, shown in the right pane at desktop width and on its own page
 * (`OwnerThreadPage`) below that. The row comes from the inbox list rather than its own request:
 * the list is already cached from the sidebar badge, and everything the header needs is on it.
 */
export function ThreadBody({ row }: { row: ConversationSummary }) {
  const navigate = useNavigate();
  const { label } = useLabels();
  const notice = useOwnerNotice();
  const isChannel = row.kind === "announcement";

  return (
    <>
      {notice.value ? (
        <SuccessNotice
          title={notice.value.title}
          message={notice.value.message}
          onDismiss={notice.clear}
        />
      ) : null}

      {!isChannel && row.counterpartPhoneE164 ? (
        <p className="pb-4 text-[14px] text-ink-2">
          <a
            href={`tel:${row.counterpartPhoneE164}`}
            className="font-semibold text-accent-strong tabular-nums"
          >
            {row.counterpartPhoneE164}
          </a>
        </p>
      ) : null}

      {isChannel ? (
        <ConversationThread
          conversation={row}
          emptyTitle="Nothing posted yet"
          emptyMessage={`Announcements go to every active ${label("member")}. They cannot reply here; replies come to their private thread with you.`}
          footer={
            <Button fullWidth onClick={() => void navigate({ to: "/owner/inbox/announce" })}>
              <MegaphoneIcon size={18} />
              New announcement
            </Button>
          }
        />
      ) : (
        <ConversationThread
          conversation={row}
          emptyTitle="Start the conversation"
          emptyMessage={`Only this ${label("member")} and your ${label("staff", { plural: true })} can see this thread.`}
          footer={
            <Composer conversationId={row.id} placeholder={`Message ${row.counterpartName}`} />
          }
        />
      )}
    </>
  );
}

/** The thread as its own page, below 1024px, with a back link to the list. */
export function OwnerThreadPage({ conversationId }: { conversationId: string }) {
  const { business, managed, isLoading, error } = useActiveBusiness();
  const conversations = useConversations();

  const row = (conversations.data ?? []).find((candidate) => candidate.id === conversationId);
  const back = { to: "/owner/inbox", label: "Inbox" };
  const gate = { business, isLoading, error, managedCount: managed.length, back };

  if (!row && conversations.isLoading) {
    return (
      <OwnerGate title="Chat" {...gate}>
        <SkeletonList rows={5} />
      </OwnerGate>
    );
  }

  if (!row) {
    return (
      <OwnerGate title="Chat" {...gate}>
        <ErrorState
          // A list that loaded without this id is the same situation as the API's own 404 for it.
          error={conversations.error ?? { code: "CONVERSATION_NOT_FOUND" }}
          onRetry={() => void conversations.refetch()}
        />
      </OwnerGate>
    );
  }

  return (
    <OwnerGate title={row.kind === "announcement" ? "Announcements" : row.counterpartName} {...gate}>
      {/* The member's phone line sits above the thread and adds to the header (`--thread-chrome`). */}
      <div
        className={[
          "-mt-2",
          row.kind !== "announcement" && row.counterpartPhoneE164
            ? "[--thread-chrome:13rem]"
            : "[--thread-chrome:10.7rem]",
        ].join(" ")}
      >
        <ThreadBody row={row} />
      </div>
    </OwnerGate>
  );
}
